import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ConfirmationNumber as TicketIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Language as WebsiteIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api, { extractData } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  account_id: string;
  account_name: string;
  notes: string;
  ticket_count: string;
  last_ticket_at: string;
  created_at: string;
}

interface Account {
  id: string;
  name: string;
  domain: string;
  phone: string;
  description: string;
  industry: string;
  website: string;
  contact_count: string;
  created_at: string;
}

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  accountId: string;
  notes: string;
}

interface AccountForm {
  name: string;
  domain: string;
  phone: string;
  description: string;
  industry: string;
  website: string;
}

const ALPHABET = 'ALL,A,B,C,D,E,F,G,H,I,J,K,L,M,N,O,P,Q,R,S,T,U,V,W,X,Y,Z'.split(',');

const HelpdeskCustomers: React.FC = () => {
  const navigate = useNavigate();
  const basePath = isHelpdeskMode() ? '/support' : '/helpdesk';
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [tab, setTab] = useState(0); // 0 = Contact, 1 = Account
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeLetter, setActiveLetter] = useState('ALL');

  // Detail view state
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [contactTickets, setContactTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'contact' | 'account'>('contact');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState<ContactForm>({ name: '', email: '', phone: '', company: '', accountId: '', notes: '' });
  const [accountForm, setAccountForm] = useState<AccountForm>({ name: '', domain: '', phone: '', description: '', industry: '', website: '' });
  const [saving, setSaving] = useState(false);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (activeLetter !== 'ALL') params.set('letter', activeLetter);
      const res = await api.get(`/support/customers/contacts?${params.toString()}`);
      const data = extractData(res) as any;
      setContacts(data.contacts || []);
      setTotalContacts(data.total || 0);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const res = await api.get(`/support/customers/accounts?${params.toString()}`);
      const data = extractData(res) as any;
      setAccounts(data.accounts || []);
      setTotalAccounts(data.total || 0);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactTickets = async (contactId: string) => {
    try {
      setLoadingTickets(true);
      const res = await api.get(`/support/customers/contacts/${contactId}/tickets`);
      setContactTickets(extractData(res) || []);
    } catch { setContactTickets([]); }
    finally { setLoadingTickets(false); }
  };

  useEffect(() => {
    if (tab === 0) fetchContacts();
    else fetchAccounts();
  }, [tab, search, activeLetter]);

  const handleSaveContact = async () => {
    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/support/customers/contacts/${editingId}`, contactForm);
      } else {
        await api.post('/support/customers/contacts', contactForm);
      }
      setDialogOpen(false);
      fetchContacts();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAccount = async () => {
    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/support/customers/accounts/${editingId}`, accountForm);
      } else {
        await api.post('/support/customers/accounts', accountForm);
      }
      setDialogOpen(false);
      fetchAccounts();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/support/customers/contacts/${id}`);
      setSelectedContact(null);
      fetchContacts();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to delete'); }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Delete this account? Contacts will be unlinked.')) return;
    try {
      await api.delete(`/support/customers/accounts/${id}`);
      fetchAccounts();
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to delete'); }
  };

  const openCreateContact = () => {
    setDialogMode('contact');
    setEditingId(null);
    setContactForm({ name: '', email: '', phone: '', company: '', accountId: '', notes: '' });
    setDialogOpen(true);
  };

  const openEditContact = (c: Contact) => {
    setDialogMode('contact');
    setEditingId(c.id);
    setContactForm({ name: c.name, email: c.email, phone: c.phone || '', company: c.company || '', accountId: c.account_id || '', notes: c.notes || '' });
    setDialogOpen(true);
  };

  const openCreateAccount = () => {
    setDialogMode('account');
    setEditingId(null);
    setAccountForm({ name: '', domain: '', phone: '', description: '', industry: '', website: '' });
    setDialogOpen(true);
  };

  const openEditAccount = (a: Account) => {
    setDialogMode('account');
    setEditingId(a.id);
    setAccountForm({ name: a.name, domain: a.domain || '', phone: a.phone || '', description: a.description || '', industry: a.industry || '', website: a.website || '' });
    setDialogOpen(true);
  };

  const getInitials = (name: string) => name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const statusColors: Record<string, string> = {
    open: '#3b82f6', in_progress: '#f59e0b', resolved: '#10b981', closed: '#6b7280',
  };

  // Contact Detail View
  if (selectedContact) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton size="small" onClick={() => setSelectedContact(null)}>
            <BackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>Contact Details</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEditContact(selectedContact)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDeleteContact(selectedContact.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Contact Info Card */}
            <Paper sx={{ flex: '1 1 340px', p: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }} elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: '#6366f1', fontSize: '1.2rem', fontWeight: 600 }}>
                  {getInitials(selectedContact.name)}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>{selectedContact.name}</Typography>
                  {selectedContact.company && <Typography sx={{ color: '#6b7280', fontSize: '0.85rem' }}>{selectedContact.company}</Typography>}
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <EmailIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                  <Typography sx={{ fontSize: '0.9rem' }}>{selectedContact.email}</Typography>
                </Box>
                {selectedContact.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                    <Typography sx={{ fontSize: '0.9rem' }}>{selectedContact.phone}</Typography>
                  </Box>
                )}
                {selectedContact.account_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BusinessIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                    <Typography sx={{ fontSize: '0.9rem' }}>{selectedContact.account_name}</Typography>
                  </Box>
                )}
                {selectedContact.notes && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <NotesIcon sx={{ fontSize: 18, color: '#6b7280', mt: 0.3 }} />
                    <Typography sx={{ fontSize: '0.85rem', color: '#4b5563' }}>{selectedContact.notes}</Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>Tickets</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>{selectedContact.ticket_count || 0}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>Since</Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                    {formatDistanceToNow(parseISO(selectedContact.created_at), { addSuffix: true })}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Contact Tickets */}
            <Paper sx={{ flex: '2 1 500px', p: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }} elevation={0}>
              <Typography sx={{ fontWeight: 600, mb: 2 }}>Tickets ({contactTickets.length})</Typography>
              {loadingTickets ? (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
              ) : contactTickets.length === 0 ? (
                <Typography sx={{ color: '#9ca3af', fontSize: '0.9rem', py: 4, textAlign: 'center' }}>No tickets found for this contact</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {contactTickets.map((t: any) => (
                    <Box
                      key={t.id}
                      onClick={() => navigate(`${basePath}/tickets/${t.id}`)}
                      sx={{
                        p: 1.5, borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer',
                        '&:hover': { bgcolor: '#f8fafc', borderColor: '#93c5fd' },
                        display: 'flex', alignItems: 'center', gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.subject}
                        </Typography>
                        <Typography sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                          #{t.ticket_number} · {formatDistanceToNow(parseISO(t.created_at), { addSuffix: true })}
                        </Typography>
                      </Box>
                      <Chip
                        label={t.status.replace('_', ' ')}
                        size="small"
                        sx={{
                          height: 22, fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize',
                          bgcolor: `${statusColors[t.status] || '#6b7280'}18`,
                          color: statusColors[t.status] || '#6b7280',
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{
        px: 3, py: 1.5, bgcolor: '#fff', borderBottom: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0,
      }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSearch(''); setActiveLetter('ALL'); }} sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0 } }}>
          <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Contact" sx={{ fontSize: '0.85rem', textTransform: 'none' }} />
          <Tab icon={<BusinessIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Account" sx={{ fontSize: '0.85rem', textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ flex: 1 }} />

        <Box sx={{
          display: 'flex', alignItems: 'center', bgcolor: '#f3f4f6',
          borderRadius: '8px', px: 1.5, py: 0.25, maxWidth: 260,
        }}>
          <SearchIcon sx={{ color: '#9ca3af', fontSize: 18, mr: 0.5 }} />
          <TextField
            size="small"
            placeholder={tab === 0 ? 'Search contacts...' : 'Search accounts...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{ '& input': { fontSize: '0.85rem', py: 0.5 } }}
          />
        </Box>

        <Chip
          label={tab === 0 ? `Total Count: ${totalContacts}` : `Total Count: ${totalAccounts}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.78rem' }}
        />

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => tab === 0 ? fetchContacts() : fetchAccounts()}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={tab === 0 ? openCreateContact : openCreateAccount}
          sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.85rem' }}
        >
          {tab === 0 ? 'Add Contact' : 'Add Account'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 3, mt: 1 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar for Contacts Tab: Tabs */}
        {tab === 0 && (
          <Box sx={{
            width: 44, bgcolor: '#fff', borderRight: '1px solid #e5e7eb',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            py: 0.5, overflow: 'auto',
            '&::-webkit-scrollbar': { width: 0 },
          }}>
            {ALPHABET.map((letter) => (
              <Box
                key={letter}
                onClick={() => setActiveLetter(letter)}
                sx={{
                  width: 30, height: letter === 'ALL' ? 28 : 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', borderRadius: '4px', my: 0.1,
                  fontSize: letter === 'ALL' ? '0.6rem' : '0.72rem',
                  fontWeight: activeLetter === letter ? 700 : 400,
                  color: activeLetter === letter ? '#3b82f6' : '#6b7280',
                  bgcolor: activeLetter === letter ? '#eff6ff' : 'transparent',
                  '&:hover': { bgcolor: '#f3f4f6' },
                }}
              >
                {letter}
              </Box>
            ))}
          </Box>
        )}

        {/* List */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fff' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : tab === 0 ? (
            /* Contacts List */
            contacts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <PersonIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                <Typography color="text.secondary">No contacts found</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {contacts.map((c) => (
                  <ListItemButton
                    key={c.id}
                    onClick={() => { setSelectedContact(c); fetchContactTickets(c.id); }}
                    sx={{
                      py: 1.5, px: 3, borderBottom: '1px solid #f3f4f6',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#6366f1', width: 40, height: 40, fontSize: '0.85rem' }}>
                        {getInitials(c.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{c.name}</Typography>
                          {c.company && (
                            <Typography sx={{ color: '#6b7280', fontSize: '0.8rem' }}>· {c.company}</Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.25 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>{c.email}</Typography>
                          {c.phone && <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>· {c.phone}</Typography>}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {parseInt(c.ticket_count || '0') > 0 && (
                        <Badge badgeContent={c.ticket_count} color="primary" max={99}>
                          <TicketIcon sx={{ fontSize: 20, color: '#9ca3af' }} />
                        </Badge>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditContact(c); }}>
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteContact(c.id); }}>
                          <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )
          ) : (
            /* Accounts List */
            accounts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <BusinessIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                <Typography color="text.secondary">No accounts found</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {accounts.map((a) => (
                  <ListItemButton
                    key={a.id}
                    sx={{
                      py: 1.5, px: 3, borderBottom: '1px solid #f3f4f6',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#10b981', width: 40, height: 40, fontSize: '0.85rem' }}>
                        {getInitials(a.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{a.name}</Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.25 }}>
                          {a.domain && <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>{a.domain}</Typography>}
                          {a.industry && <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>· {a.industry}</Typography>}
                          {a.phone && <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>· {a.phone}</Typography>}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${a.contact_count || 0} contacts`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', height: 24 }}
                      />
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditAccount(a); }}>
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteAccount(a.id); }}>
                          <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            )
          )}
        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editingId ? 'Edit' : 'Add'} {dialogMode === 'contact' ? 'Contact' : 'Account'}
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dialogMode === 'contact' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField label="Name" required fullWidth size="small" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
              <TextField label="Email" required fullWidth size="small" type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
              <TextField label="Phone" fullWidth size="small" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
              <TextField label="Company" fullWidth size="small" value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} />
              {accounts.length > 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Account</InputLabel>
                  <Select label="Account" value={contactForm.accountId} onChange={(e) => setContactForm({ ...contactForm, accountId: e.target.value })}>
                    <MenuItem value="">None</MenuItem>
                    {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <TextField label="Notes" fullWidth multiline rows={3} size="small" value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField label="Company Name" required fullWidth size="small" value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} />
              <TextField label="Domain" fullWidth size="small" placeholder="e.g. ekedtronics.com" value={accountForm.domain} onChange={(e) => setAccountForm({ ...accountForm, domain: e.target.value })} />
              <TextField label="Phone" fullWidth size="small" value={accountForm.phone} onChange={(e) => setAccountForm({ ...accountForm, phone: e.target.value })} />
              <TextField label="Industry" fullWidth size="small" value={accountForm.industry} onChange={(e) => setAccountForm({ ...accountForm, industry: e.target.value })} />
              <TextField label="Website" fullWidth size="small" value={accountForm.website} onChange={(e) => setAccountForm({ ...accountForm, website: e.target.value })} />
              <TextField label="Description" fullWidth multiline rows={3} size="small" value={accountForm.description} onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={dialogMode === 'contact' ? handleSaveContact : handleSaveAccount}
            disabled={saving || (dialogMode === 'contact' ? !contactForm.name || !contactForm.email : !accountForm.name)}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HelpdeskCustomers;
